/** Mirrors Go's templates.UnfilledPlaceholder (internal/outreach/templates/placeholder.go): the same shapes,
 *  so a draft the wizard lets through is one approve will not skip as has_placeholder. */
const UNFILLED_RES: readonly RegExp[] = [
  /\{\{[^{}]{1,80}\}\}/,
  /\{[A-Za-z_][A-Za-z0-9_]{0,40}\}/,
  /\[\[[^[\]]{1,80}\]\]/,
  /<<[^<>]{1,80}>>/,
  /\[\s*(?:your|insert|enter|add|fill|specify|tbd|todo|x{2,}|(?:first\s+|last\s+|creator\s+|brand\s+|campaign\s+)?name|amount|rate|price|fee|budget|deliverables?|date|brand|campaign|platform|handle|creator)\b[^[\]]{0,60}\]/i,
];

const BLANK_RE = /_{3,}/g;
const MAX_REPORTED = 40;
const INVISIBLE = new Set([0x200b, 0x200c, 0x200d, 0x2060, 0xfeff, 0x00ad].map((code) => String.fromCodePoint(code)));
const NAMED_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: String.fromCodePoint(0xa0) };

function decodeEntities(text: string): string {
  return text.replace(/&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|([A-Za-z]+));/g, (whole, dec?: string, hex?: string, name?: string) => {
    if (name) return NAMED_ENTITIES[name] ?? whole;
    const code = dec ? Number(dec) : parseInt(hex ?? '', 16);
    return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
  });
}

function normalize(text: string): string {
  const decoded = decodeEntities(Array.from(text).filter((char) => !INVISIBLE.has(char)).join(''));
  return Array.from(decoded, (char) => {
    const code = char.codePointAt(0) ?? 0;
    return code >= 0xff01 && code <= 0xff5e ? String.fromCharCode(code - 0xfee0) : char;
  }).join('');
}

const isWordChar = (char: string | undefined): boolean => !!char && (/[0-9A-Za-z_]/.test(char) || (char.codePointAt(0) ?? 0) > 0x7f);

function isInlineBlank(text: string, start: number, end: number): boolean {
  if (isWordChar(Array.from(text.slice(0, start)).pop()) || isWordChar(Array.from(text.slice(end))[0])) return false;
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const next = text.indexOf('\n', end);
  const lineEnd = next >= 0 ? next : text.length;
  return (text.slice(lineStart, start) + text.slice(end, lineEnd)).trim() !== '';
}

function clip(found: string): string {
  return Array.from(found).slice(0, MAX_REPORTED).join('');
}

function firstPlaceholder(text: string): string | undefined {
  for (const re of UNFILLED_RES) {
    const found = re.exec(text)?.[0];
    if (found) return clip(found);
  }
  for (const match of text.matchAll(BLANK_RE)) {
    const start = match.index ?? 0;
    if (isInlineBlank(text, start, start + match[0].length)) return clip(match[0]);
  }
  return undefined;
}

/** The first merge token or fill-in blank left in a draft's subject or body, if any. */
export function unfilledPlaceholder(subject: string, body: string): string | undefined {
  return firstPlaceholder(normalize(subject)) ?? firstPlaceholder(normalize(body));
}
